import {
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { type SortState, sortRows } from "./sort.js";

/**
 * Минимальный набор элементов. Без внешних UI-китов. Одна вёрстка на телефон и широкий экран:
 * раскладка меняется брейкпоинтами Tailwind (`lg:` от 1024px), а там, где на широком экране нужно
 * смонтировать другое дерево (две колонки вместо одной), компонент спрашивает `useWide()`.
 */

/** Широкий экран: тот же порог, что у `lg:` в Tailwind. */
export const WIDE_QUERY = "(min-width: 1024px)";
/** Ещё шире (`xl:`): хватает места на третью колонку. */
export const EXTRA_WIDE_QUERY = "(min-width: 1280px)";
/** Устройство с мышью и клавиатурой: Enter отправляет, подсказки про клавиши. */
export const POINTER_QUERY = "(hover: hover) and (pointer: fine)";

/** Функция подписки на каждый запрос одна: `useSyncExternalStore` переподписывается, если она меняется. */
const subscribers = new Map<string, (onChange: () => void) => () => void>();
const subscribeMedia = (query: string) => {
  let subscribe = subscribers.get(query);
  if (!subscribe) {
    subscribe = (onChange) => {
      if (typeof window === "undefined") return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    };
    subscribers.set(query, subscribe);
  }
  return subscribe;
};

/** Совпадает ли медиазапрос; обновляется при изменении размера окна. */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    subscribeMedia(query),
    () => window.matchMedia(query).matches,
    () => false,
  );
}

export const useWide = (): boolean => useMediaQuery(WIDE_QUERY);

export * from "./icons.js";
export { emptyLast, type SortState, sortRows, textComparator } from "./sort.js";

/** Видимый фокус с клавиатуры (мышь и касание его не показывают). */
export const focusRing = "outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2";

const buttonBase = `inline-flex items-center justify-center rounded-xl px-4 py-3 text-base font-medium transition active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none ${focusRing}`;
const buttonVariants = {
  primary: "bg-slate-900 text-white hover:bg-slate-800",
  secondary: "bg-slate-100 text-slate-900 hover:bg-slate-200",
  ghost: "text-slate-700 hover:bg-slate-100",
  /** Необратимое действие рядом с обычными (отменить запись): та же форма, красный текст. */
  danger: "bg-red-50 text-red-700 hover:bg-red-100",
  dangerGhost: "text-red-700 hover:bg-red-50",
};
export type ButtonVariant = keyof typeof buttonVariants;

/** Классы кнопки для ссылок, которые выглядят как кнопки (кнопку внутрь ссылки класть нельзя). */
export const buttonClass = (variant: ButtonVariant = "primary", className = ""): string => `${buttonBase} ${buttonVariants[variant]} ${className}`;

export function Button({ className = "", variant = "primary", ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  return <button type="button" className={buttonClass(variant, className)} {...props} />;
}

/**
 * Поле ввода. Фокус: рамка темнеет и утолщается до 2px кольцом вплотную к рамке (`ring-1` без отступа),
 * поэтому выделение повторяет скругление поля на любом браузере, в том числе в Chrome на Android.
 */
const fieldClass =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base outline-none transition-[border-color,box-shadow] focus:border-slate-900 focus:ring-1 focus:ring-slate-900";
export function Input({ className = "", ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`${fieldClass} ${className}`} {...props} />;
}

export function Label({ children, htmlFor, className = "" }: { children: ReactNode; htmlFor?: string | undefined; className?: string }) {
  return (
    <label htmlFor={htmlFor} className={`mb-1 block text-sm text-slate-600 ${className}`}>
      {children}
    </label>
  );
}

/**
 * Поле формы с подписью и подсказкой. В узком контейнере (телефон, шторка) подпись над полем, как раньше;
 * в широком (от 32rem, контейнерный запрос `@lg`) подпись слева от поля, подсказка под полем.
 */
export function FormRow({
  label,
  htmlFor,
  hint,
  hintClass = "mt-1 text-sm text-slate-500",
  children,
}: {
  label: ReactNode;
  htmlFor?: string | undefined;
  hint?: ReactNode;
  /** Классы подсказки, если на экране она была другого размера. */
  hintClass?: string;
  children: ReactNode;
}) {
  return (
    <div className="@container">
      <div className="@lg:grid @lg:grid-cols-[12rem_minmax(0,1fr)] @lg:items-start @lg:gap-x-6">
        <Label htmlFor={htmlFor} className="@lg:mb-0 @lg:pt-3">
          {label}
        </Label>
        <div className="min-w-0">
          {children}
          {hint && <p className={hintClass}>{hint}</p>}
        </div>
      </div>
    </div>
  );
}

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-4 ${className}`}>{children}</div>;
}

export function PageTitle({ children }: { children: ReactNode }) {
  return <h1 className="mb-4 text-2xl font-semibold">{children}</h1>;
}

export function Notice({ children, kind = "info" }: { children: ReactNode; kind?: "info" | "error" | "success" }) {
  const styles = { info: "bg-slate-50 text-slate-700", error: "bg-red-50 text-red-700", success: "bg-green-50 text-green-700" }[kind];
  return <div className={`rounded-xl px-4 py-3 text-sm ${styles}`}>{children}</div>;
}

export function Spinner() {
  return <div className="mx-auto my-8 h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" role="status" aria-label="loading" />;
}

export function Screen({ children }: { children: ReactNode }) {
  return <div className="mx-auto min-h-dvh w-full max-w-md px-4 py-6">{children}</div>;
}

export function Textarea({ className = "", ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`${fieldClass} ${className}`} {...props} />;
}

export function Select({ className = "", ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={`${fieldClass} field-select ${className}`} {...props} />;
}

/** Выключатель: строка с подписью и тумблером справа. */
export function Toggle({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 py-2">
      <span>
        <span className="block">{label}</span>
        {hint && <span className="block text-sm text-slate-500">{hint}</span>}
      </span>
      <input type="checkbox" className="peer sr-only" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="relative h-7 w-12 shrink-0 rounded-full bg-slate-300 transition peer-checked:bg-slate-900 peer-disabled:opacity-50 after:absolute after:top-0.5 after:left-0.5 after:h-6 after:w-6 after:rounded-full after:bg-white after:transition peer-checked:after:translate-x-5" />
    </label>
  );
}

const tones = {
  slate: "bg-slate-100 text-slate-700",
  amber: "bg-amber-100 text-amber-800",
  green: "bg-green-100 text-green-800",
  blue: "bg-blue-100 text-blue-800",
  red: "bg-red-100 text-red-700",
} as const;
export type Tone = keyof typeof tones;

export function Badge({ children, tone = "slate" }: { children: ReactNode; tone?: Tone }) {
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>{children}</span>;
}

/** Кнопка-чип для выбора (услуги, мастер, время). */
export function Chip({ active, children, onClick, disabled }: { active?: boolean; children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={active}
      onClick={onClick}
      className={`shrink-0 rounded-full border px-4 py-2 text-sm transition disabled:opacity-40 ${focusRing} ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50"}`}
    >
      {children}
    </button>
  );
}

/** Подзаголовок секции. */
export function SectionTitle({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <h2 className={`mt-6 mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500 ${className}`}>{children}</h2>;
}

/**
 * Шторка поверх экрана (услуга, запись, отгул). На телефоне снизу, на широком окне по центру.
 * `side`: на широком экране не окно, а боковая панель справа (детали записи): страница под ней остаётся
 * видимой и кликабельной, панель закрывается крестиком и Esc.
 * Закрывается фоном, крестиком и Esc. Высота до 90% экрана, содержимое прокручивается.
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  side = false,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  side?: boolean;
}) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  // `onClose` почти всегда новая стрелка на каждый рендер родителя: держим её в ref, чтобы эффект открытия
  // (фокус внутрь, блокировка прокрутки) не перезапускался и не уводил фокус из поля при каждой перерисовке.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCloseRef.current();
    window.addEventListener("keydown", onKey);
    // Боковая панель на широком экране страницу не перекрывает, прокрутку не трогаем.
    const lock = !(side && window.matchMedia(WIDE_QUERY).matches);
    const body = document.body.style;
    const prev = { overflow: body.overflow, paddingRight: body.paddingRight };
    if (lock) {
      // Полоса прокрутки пропадает вместе с прокруткой, и страница под шторкой сдвигается вправо на её ширину:
      // возвращаем эту ширину отступом справа (к отступу выреза экрана из index.css).
      const gap = window.innerWidth - document.documentElement.clientWidth;
      body.overflow = "hidden";
      if (gap > 0) body.paddingRight = `calc(env(safe-area-inset-right) + ${gap}px)`;
    }
    // Фокус внутрь шторки при открытии и обратно на то, что было в фокусе, при закрытии.
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      if (lock) {
        body.overflow = prev.overflow;
        body.paddingRight = prev.paddingRight;
      }
      before?.focus();
    };
  }, [open, side]);
  if (!open) return null;
  return (
    <div
      className={`fixed inset-0 z-40 flex items-end justify-center sm:items-center sm:p-4 ${side ? "lg:pointer-events-none lg:items-stretch lg:justify-end lg:p-0" : ""}`}
    >
      <button type="button" aria-label="close" className={`absolute inset-0 bg-slate-900/40 ${side ? "lg:hidden" : ""}`} onClick={onClose} />
      <div
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
        aria-modal={side ? undefined : "true"}
        aria-labelledby={titleId}
        className={`relative z-10 flex max-h-[90dvh] w-full max-w-md flex-col rounded-t-3xl bg-white pb-[env(safe-area-inset-bottom)] shadow-xl outline-none sm:rounded-3xl sm:pb-0 ${
          side ? "lg:pointer-events-auto lg:max-h-none lg:w-[26rem] lg:rounded-none lg:border-l lg:border-slate-200" : ""
        }`}
      >
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div id={titleId} className="text-lg font-semibold">
            {title}
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`rounded-full px-3 py-1 text-2xl leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-700 ${focusRing}`}
            aria-label="close"
          >
            ×
          </button>
        </div>
        <div className="overflow-y-auto px-4 pb-6">{children}</div>
      </div>
    </div>
  );
}

/** Строка списка-меню: заголовок, подпись, стрелка; ведёт по ссылке или вызывает действие. */
export function MenuRow({ title, hint, right, onClick }: { title: ReactNode; hint?: ReactNode; right?: ReactNode; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between gap-3 border-b border-slate-100 py-3 text-left last:border-b-0 hover:text-slate-600 focus-visible:rounded-md ${focusRing}`}
    >
      <span className="min-w-0">
        <span className="block truncate">{title}</span>
        {hint && <span className="block truncate text-sm text-slate-500">{hint}</span>}
      </span>
      <span className="flex shrink-0 items-center gap-2 text-slate-400">
        {right}
        <span aria-hidden="true">›</span>
      </span>
    </button>
  );
}

export interface Column<T> {
  key: string;
  title: ReactNode;
  render: (row: T) => ReactNode;
  /** Сравнение для сортировки по столбцу; без него столбец не сортируется. */
  sort?: (a: T, b: T) => number;
  /** Сравнение, которое само знает направление (пустые значения в конец при любом направлении). */
  sortWithDir?: (dir: "asc" | "desc") => (a: T, b: T) => number;
  className?: string;
}

/**
 * Таблица с сортировкой по столбцам для широкого экрана (клиенты, визиты). Заголовок сортируемого столбца
 * это кнопка: первый клик по возрастанию, второй по убыванию. Строка кликабельна, выбранная подсвечена.
 */
export function DataTable<T>({
  label,
  columns,
  rows,
  rowKey,
  onRowClick,
  selectedKey,
  defaultSort,
  rowLabel,
}: {
  /** Доступное имя таблицы. */
  label: string;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectedKey?: string | null;
  defaultSort?: SortState;
  /** Доступное имя строки (кнопка в первой ячейке). */
  rowLabel?: (row: T) => string;
}) {
  const [sort, setSort] = useState<SortState | null>(defaultSort ?? null);
  const sorted = sortRows(rows, columns, sort);
  const toggle = (key: string) => setSort((cur) => (cur?.key === key ? { key, dir: cur.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
      <table className="w-full text-left text-sm" aria-label={label}>
        <thead className="text-xs uppercase tracking-wide text-slate-500">
          <tr className="border-b border-slate-200">
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={`px-4 py-2 font-semibold ${c.className ?? ""}`}
                aria-sort={sort?.key === c.key ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
              >
                {c.sort || c.sortWithDir ? (
                  <button type="button" onClick={() => toggle(c.key)} className={`inline-flex items-center gap-1 rounded hover:text-slate-900 ${focusRing}`}>
                    {c.title}
                    <span aria-hidden="true" className={sort?.key === c.key ? "" : "opacity-30"}>
                      {sort?.key === c.key && sort.dir === "desc" ? "↓" : "↑"}
                    </span>
                  </button>
                ) : (
                  c.title
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const key = rowKey(row);
            const selected = key === selectedKey;
            // Мышью кликабельна вся строка; с клавиатуры строку открывает кнопка в первой ячейке (у `tr` нет роли кнопки,
            // а кольцо фокуса на `tr` в Safari не рисуется). Выбранная строка темнее строки под курсором плюс полоса слева.
            return (
              <tr
                key={key}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`border-b border-slate-100 last:border-b-0 ${onRowClick ? "cursor-pointer hover:bg-slate-50" : ""} ${
                  selected ? "bg-slate-100 shadow-[inset_3px_0_0_#0f172a]" : ""
                }`}
              >
                {columns.map((c, i) => (
                  <td key={c.key} className={`px-4 py-2.5 align-top ${c.className ?? ""}`}>
                    {i === 0 && onRowClick ? (
                      <button
                        type="button"
                        aria-current={selected ? "true" : undefined}
                        aria-label={rowLabel?.(row)}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRowClick(row);
                        }}
                        className={`rounded text-left ${focusRing}`}
                      >
                        {c.render(row)}
                      </button>
                    ) : (
                      c.render(row)
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
